import _ from 'lodash';

import API from '../../respondent-v2/src/api';

/**
 * Determines whether an error is a QuotaExceededError
 *
 * @param err
 * @returns {boolean}
 */
function isQuotaExceededError(err) {
  return (
    err instanceof DOMException &&
    (err.name === 'QUOTA_EXCEEDED_ERR' ||
      // Firefox
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

/**
 * Determines whether a storage implementing the Web Storage API (localStorage or sessionStorage) is supported
 *
 * @param webStorageType (default: 'localStorage')
 * @returns {boolean}
 */
function isStorageSupported(webStorageType = 'localStorage') {
  let storage;
  try {
    storage = window[webStorageType];
    if (!storage) {
      return false;
    }
    const x = `__storage_test__`;
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (err) {
    const isValidQuotaExceededError =
      isQuotaExceededError(err) && storage.length > 0;
    if (isValidQuotaExceededError) {
      logError(false);
    }
    return isValidQuotaExceededError;
  }
}

/**
 * Retrieves a value from localStorage with no deserialization.
 * @callback get
 * @returns {*}
 */
const get = (key) => () => localStorage.getItem(key);

const getJSONWithDefault = (getDefault) => (key) => () => {
  try {
    return JSON.parse(localStorage.getItem(key)) || getDefault();
  } catch (error) {
    return getDefault();
  }
};

/**
 * Retrieves a JSON string from localStorage and parses it.
 * Returns an empty object when the key is not found or when it is invalid JSON.
 * @callback getObject
 * @returns {Object.<string, any>}
 */
const getObject = getJSONWithDefault(() => ({}));

/**
 * Retrieves a JSON string from localStorage and parses it.
 * Returns an empty array when the key is not found or when it is invalid JSON.
 * @callback getArray
 * @returns {Array<any>}
 */
const getArray = getJSONWithDefault(() => []);

/**
 * Sets a value in localStorage with no serialization.
 * @callback set
 * @param value the value to be set
 * @returns {void}
 */
const set = (key) => (value) => {
  if (!isStorageSupported()) {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    logError(true);
    return false;
  }
};

/**
 * Removes a value at a key in localStorage.
 * @callback remove
 * @returns {void}
 */
const remove = (key) => () => {
  localStorage.removeItem(key);
};

/**
 * Merges an object into a nested JSON string in local storage.
 * Uses lodash's {@link https://lodash.com/docs/#merge _.merge()} internally.
 * @callback merge
 * @param {object} value the object you are merging into the existing storage.
 * @returns {void}
 */
const merge = (key) => (value) => {
  const previous = getObject(key)();
  const next = JSON.stringify(_.merge(previous, value));
  localStorage.setItem(key, next);
};

/**
 * Sets a value in a nested JSON string in local storage.
 * Uses lodash's {@link https://lodash.com/docs/#set _.set()} internally.
 * @callback setInObject
 * @param {string} path the path of keys you are setting
 * @param {any} value the value to be set at the path
 * @returns {void}
 */
const setInObject = (key) => (path, value) => {
  if (!isStorageSupported()) {
    return;
  }
  try {
    const previous = getObject(key)();
    const next = JSON.stringify(_.set(previous, path, value));
    localStorage.setItem(key, next);
  } catch (e) {
    logError(true);
    return false;
  }
};

/**
 * Removes a value in a nested JSON string in local storage.
 * Uses lodash's {@link https://lodash.com/docs/#set _.set()} internally.
 * @callback removeInObject
 * @param {string} path the path of keys you are removing
 * @returns {void}
 */
const removeInObject = (key) => (path) => {
  if (!isStorageSupported()) {
    return;
  }
  try {
    const localStorageNestedValue = getObject(key)();
    delete localStorageNestedValue[path];
    localStorage.setItem(key, JSON.stringify(localStorageNestedValue));
  } catch (e) {
    logError(true);
    return false;
  }
};

/**
 * Calls JSON.stringify() on a value and sets it in local storage.
 * @callback setJSON
 * @param {Object.<string, any>} value the value to be set at the key.
 * @returns {void}
 */
const setJSON = (key) => (value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    logError(true);
    return false;
  }
};

/**
 * Calls JSON.parse() on a value and returns it.
 * @callback getJSON
 * @returns {*}
 */
const getJSON = (key) => () => {
  return JSON.parse(localStorage.getItem(key));
};

/**
 * Creates getters and setters for a localStorage key.
 * @param {string} key localStorage key to bind the helpers to
 * @returns {{
 * 	get: get,
 * 	getObject: getObject,
 * 	getArray: getArray,
 * 	merge: merge,
 * 	set: set,
 * 	setJSON: setJSON,
 * 	setInObject: setInObject
 * }} utility functions for getting and setting a value in local storage
 */
const createHelpers = (key) => ({
  get: get(key),
  getJSON: getJSON(key),
  getArray: getArray(key),
  getObject: getObject(key),
  merge: merge(key),
  set: set(key),
  setJSON: setJSON(key),
  setInObject: setInObject(key),
  removeInObject: removeInObject(key),
  remove: remove(key),
});

const composeFullParticipantLocalStorageObject = () => {
  return {
    participantSignup: localStorage.getItem('participantSignup'),
    researcherOnboarding: localStorage.getItem('researcherOnboarding'),
    respondentInvite: localStorage.getItem('respondent.invite'),
    respondentOrganizationId: localStorage.getItem('respondent.organizationId'),
    respondentReferralCode: localStorage.getItem('respondent.referralCode'),
    respondentReferralProject: localStorage.getItem(
      'respondent.referralProject'
    ),
    respondentTeamId: localStorage.getItem('respondent.teamId'),
    respondentRecentProjectSearches: localStorage.getItem(
      'respondent.recentProjectSearches'
    ),
    respondentGuidedTourCompletion: localStorage.getItem(
      'respondent.guidedTourCompletion'
    ),
    userPreferences: localStorage.getItem('userPreferences'),
    redirect: localStorage.getItem('redirect'),
    respondentStore: localStorage.getItem('respondent.store'),
  };
};

const logError = (localStorageSupported) => {
  API.post('v2/logger', {
    level: 'debug',
    message: 'localStorageError',
    body: {
      localStorageSupported,
      participantLocalStorage: JSON.stringify(
        composeFullParticipantLocalStorageObject
      ),
    },
  });
};

/**
 * Helpers bound to keys
 */
export default {
  participantSignup: createHelpers('participantSignup'),
  researcherOnboarding: createHelpers('researcherOnboarding'),
  respondentInvite: createHelpers('respondent.invite'),
  respondentOrganizationId: createHelpers('respondent.organizationId'),
  respondentReferralCode: createHelpers('respondent.referralCode'),
  respondentReferralProject: createHelpers('respondent.referralProject'),
  respondentTeamId: createHelpers('respondent.teamId'),
  respondentRecentProjectSearches: createHelpers(
    'respondent.recentProjectSearches'
  ),
  respondentGuidedTourCompletion: createHelpers(
    'respondent.guidedTourCompletion'
  ),
  userPreferences: createHelpers('userPreferences'),
  redirect: createHelpers('redirect'),
  respondentStore: createHelpers('respondent.store'),
};
